import {
  isEmptyString,
  getDeviceType,
  DeviceType,
  throttle,
  isOutOfViewport,
  clearClassList,
  collapseContainer,
  expandContainer,
  fadeInOutContainer,
  getHeightDisplayNone,
  setSameMinHeightOnItems,
  queryAll,
  GetYOutOfViewport,
} from "@shared/Utils";
import { scrollTo } from "@shared/scroll-to";

export enum FaqIds {
  card = "#faqcard-",
  container = "#faq-container-",
}
export enum RowExpandStatus {
  AddNew,
  Expand,
  Collapse,
  RemoveAndAddNew,
}
export interface ElementStatus {
  item: Element | null;
  status: RowExpandStatus;
}

class FaqExpandable {
  private currentlyTogglingContainer?: Element;
  private devicetype?: DeviceType;

  public bootstrap(): void {
    const faqExpandableCards = queryAll<HTMLElement>(".faq-expandable-card");

    this.devicetype = getDeviceType();
    if (this.devicetype === DeviceType.Desktop) {
      this.setSameHeightOnAllItemsInRow(faqExpandableCards);
      window.addEventListener("resize", throttle(this.resetColorArrowPosition, 100), { passive: true });
    }

    faqExpandableCards.forEach((card) => this.initExpandEvent(card));

    this.openLinkedCard(faqExpandableCards);
  }

  public initExpandEvent(card: HTMLElement): void {
    const container = this.getFaqElement(card, FaqIds.container);
    if (!container) return;

    if (isEmptyString(container?.querySelector(".faq-margin-container .faq-content")?.innerHTML)) {
      const arrow = card.querySelector(".faq-arrow");
      if (arrow) arrow.classList.add("faq-hide");
      card.classList.add("show-default-cursor");
    } else if (this.devicetype !== DeviceType.Desktop) {
      const closeArrow = container?.querySelector(".js-faq-arrow-close");
      if (closeArrow)
        closeArrow.addEventListener("click", (): void => {
          this.collapseMobileContainer(card, container)
            .then((): void => {
              this.currentlyTogglingContainer = undefined;
              this.toggleExpandIcon(card);
            })
            .catch((e) => console.error(e));
        });
    }

    card.addEventListener("click", (): void => {
      this.toggleContainer(container, card)
        .finally(() => {
          this.scrollToExpandedItem(container, card);
        })
        .catch(console.error);
    });
  }

  public openLinkedCard(faqExpandableCards: Array<HTMLElement>): void {
    const queryParameters = new URLSearchParams(window.location.search);
    const faqFriendlyName = queryParameters.get("faq");
    if (!faqFriendlyName) return;

    const linkedCard = faqExpandableCards.find(
      (card: HTMLElement) => card.dataset.friendlyName?.toLowerCase() === faqFriendlyName.toLowerCase(),
    );
    if (!linkedCard) return;

    const container = this.getFaqElement(linkedCard, FaqIds.container);
    if (!container) return;

    if (this.devicetype === DeviceType.Desktop) {
      this.toggleContainer(container, linkedCard)
        .finally(() => {
          this.scrollToExpandedItem(container, linkedCard);
        })
        .catch(console.error);
    } else {
      this.toggleContainerMobile(container, linkedCard)
        .finally(() => {
          this.scrollToExpandedItem(container, linkedCard);
        })
        .catch(console.error);
    }
  }

  private toggleContainer(container: HTMLElement, card: HTMLElement): Promise<string | void> {
    if (this.currentlyTogglingContainer === container) {
      return Promise.resolve(undefined);
    }
    this.currentlyTogglingContainer = container;

    if (this.devicetype === DeviceType.Desktop && container.closest(".grid-row") !== null) {
      const [rowArea, rowContainer] = this.getRowBlockArea(container);
      if (!rowArea || !rowContainer) return Promise.resolve(undefined);

      const arrow = this.addArrowUp(rowArea) as HTMLElement;

      rowContainer.setAttribute("aria-hidden", "true");
      const elementstatus = this.getElementStatus(rowContainer, container);
      rowArea.style.display = "block";

      switch (elementstatus.status) {
        case RowExpandStatus.AddNew:
          return this.copyNewItem(card, rowContainer, container, arrow)
            .then((): void => {
              this.currentlyTogglingContainer = undefined;
            })
            .catch((e) => console.error(e));

        case RowExpandStatus.Collapse:
          return this.collapseItem(card, rowContainer, container, elementstatus, arrow)
            .then((): void => {
              this.currentlyTogglingContainer = undefined;
            })
            .catch((e) => console.error(e));
        case RowExpandStatus.RemoveAndAddNew:
          return this.collapseAndChangeItem(card, rowContainer, container, elementstatus, arrow)
            .then((): void => {})
            .catch((e) => console.error(e));
      }
    } else {
      return this.toggleContainerMobile(container, card)
        .then((): void => {
          this.currentlyTogglingContainer = undefined;
        })
        .catch((e) => console.error(e));
    }

    return Promise.resolve();
  }

  private toggleContainerMobile(container: HTMLElement, card: HTMLElement): Promise<string | void> {
    this.toggleExpandIcon(card);
    if (container.classList.contains("faq-hide")) {
      const height = getHeightDisplayNone(container);
      card.classList.add("selected");
      return expandContainer(container, height).then((): void => {
        card.setAttribute("aria-expanded", "true");
      });
    } else {
      return this.collapseMobileContainer(card, container);
    }
  }

  private copyNewItem(
    card: Element,
    rowContainer: HTMLElement,
    container: HTMLElement,
    arrow: HTMLElement,
  ): Promise<string | void> {
    this.copyColorStyle(container, rowContainer);

    if (arrow.parentElement) arrow.parentElement.style.height = "";

    const borderColorcss = container.querySelector(".faq-expandable-arrow")?.getAttribute("border-color-class") ?? "";

    this.setArrowPosition(card, arrow);

    this.toggleExpandIcon(card);
    const clone = this.copyItemToRowContainer(rowContainer, container) as HTMLElement;

    card.classList.add("selected");
    card.setAttribute("aria-expanded", "true");
    container.classList.add("visually-hidden");
    container.classList.remove("faq-hide");

    const elementHeight = getHeightDisplayNone(clone);
    return expandContainer(clone, elementHeight).then((): void => {
      this.resetArrowCSS(arrow, [borderColorcss]);
      rowContainer.style.height = `${elementHeight}px`;
    });
  }

  private collapseItem(
    card: Element,
    rowContainer: HTMLElement,
    container: HTMLElement,
    elementstatus: ElementStatus,
    arrow: HTMLElement,
  ): Promise<string | void> {
    card.classList.remove("selected");
    card.setAttribute("aria-expanded", "false");
    this.toggleExpandIcon(card);
    this.resetArrowCSS(arrow, ["faq-arrow-hide"]);
    rowContainer.style.height = "";
    if (arrow.parentElement) arrow.parentElement.style.height = "0px";

    container.classList.remove("visually-hidden");
    container.classList.add("faq-hide");

    return collapseContainer(elementstatus.item as HTMLElement).then((): void => {
      this.removeColorStyle(elementstatus.item, rowContainer);
      rowContainer.innerHTML = "";
    });
  }

  private collapseAndChangeItem(
    card: Element,
    rowContainer: HTMLElement,
    container: HTMLElement,
    elementstatus: ElementStatus,
    arrow: Element,
  ): Promise<string | void> {
    const lastcard = this.getFaqElement(elementstatus.item, FaqIds.card);
    if (!lastcard) return Promise.resolve();
    lastcard.classList.remove("selected");
    lastcard.setAttribute("aria-expanded", "false");

    const lastContainer = this.getFaqElement(lastcard, FaqIds.container);
    if (!lastContainer) return Promise.resolve();
    lastContainer.classList.remove("visually-hidden");
    lastContainer.classList.add("faq-hide");

    this.toggleExpandIcon(lastcard);
    return fadeInOutContainer(elementstatus.item as HTMLElement, true).then((): Promise<string | void> => {
      this.removeColorStyle(elementstatus.item, rowContainer);
      rowContainer.innerHTML = "";
      card.classList.add("selected");
      this.toggleExpandIcon(card);
      const newclone = this.copyItemToRowContainer(rowContainer, container) as HTMLElement;

      container.classList.add("visually-hidden");
      container.classList.remove("faq-hide");
      const elementHeight = getHeightDisplayNone(newclone);
      setTimeout((): void => {
        rowContainer.style.height = `${elementHeight}px`;
      }, 0);

      this.copyColorStyle(newclone, rowContainer);
      const borderColorcss = container.querySelector(".faq-expandable-arrow")?.getAttribute("border-color-class");
      if (borderColorcss) this.resetArrowCSS(arrow, [borderColorcss]);
      this.setArrowPosition(card, arrow as HTMLElement);
      card.setAttribute("aria-expanded", "true");

      return fadeInOutContainer(newclone, false);
    });
  }

  private collapseMobileContainer(card: HTMLElement, container: HTMLElement | null): Promise<string | void> {
    if (!container) return Promise.resolve();

    card.classList.remove("selected");

    return collapseContainer(container).then((): void => {
      card.setAttribute("aria-expanded", "false");
      card.focus();
    });
  }

  private getFaqElement(el: Element | null, id: FaqIds): HTMLElement | null {
    if (!el) return null;
    return document.querySelector<HTMLElement>(`${id}${el.getAttribute("idRef")}`);
  }

  private toggleExpandIcon(card: Element): void {
    const iconContainer = card.querySelector(".faq-arrow");
    if (!iconContainer) return;

    const arrows = [...iconContainer.querySelectorAll<HTMLElement>(".faq-arrow-do-expand")];
    if (!arrows.length) return;
    arrows.forEach((arrow: HTMLElement) => {
      arrow.classList.toggle("faq-arrow-hide");
    });
  }

  private getRowBlockArea(item: Element): HTMLElement[] {
    const rowContainer = item.closest(".grid-row");
    const rowId = rowContainer?.getAttribute("blockid");
    const row = rowContainer?.parentNode as HTMLElement;

    const rowExpandarea = row.querySelector<HTMLElement>(`[name='rowblock-expand-area-${rowId}']`);
    const rowBlockContainer = rowExpandarea?.querySelector<HTMLElement>(".row-block-container");

    return [rowExpandarea as HTMLElement, rowBlockContainer as HTMLElement];
  }

  private copyItemToRowContainer(rowContainer: Element, item: Element): Element {
    const cloned = item.cloneNode(true) as Element;
    rowContainer.appendChild(cloned);
    return cloned;
  }

  private getElementStatus(rowContainer: HTMLElement, container: HTMLElement): ElementStatus {
    const firstChild = rowContainer.querySelector(".faq-expandable-container");
    if (firstChild === null) {
      return { item: firstChild, status: RowExpandStatus.AddNew };
    }

    if (firstChild.getAttribute("idRef") === container.getAttribute("idRef")) {
      if (firstChild.classList.contains("faq-hide")) {
        return { item: firstChild, status: RowExpandStatus.Expand };
      }
      return { item: firstChild, status: RowExpandStatus.Collapse };
    }
    return { item: firstChild, status: RowExpandStatus.RemoveAndAddNew };
  }

  private copyColorStyle(item: Element, container: Element): string {
    const cssStyle = item.getAttribute("background-color-class") ?? "";
    container.classList.add(cssStyle);
    return cssStyle;
  }

  private removeColorStyle(item: Element | null, container: Element): void {
    if (!item) return;

    const cssStyle = item.getAttribute("background-color-class") ?? "";
    container.classList.remove(cssStyle);
  }

  private addArrowUp(container: Element): Element | null {
    const arrowDiv = container.querySelector(".js-arrow");
    if (arrowDiv !== null) {
      return arrowDiv.querySelector(".faq-color-arrow");
    }

    const div = document.createElement("div") as HTMLElement;
    div.classList.add("js-arrow");
    div.classList.add("faq-wide-arrow-container");

    container.prepend(div);

    const arrow = document.createElement("div");
    arrow.classList.add("faq-color-arrow");
    div.appendChild(arrow);
    return arrow;
  }

  private resetArrowCSS(arrow: Element, cssList: string[]): void {
    clearClassList(arrow);
    arrow.classList.add("faq-color-arrow");

    cssList.forEach((classItem): void => {
      arrow.classList.add(classItem);
    });
  }

  private getArrowPosition(card: Element): number {
    const rect = card.getBoundingClientRect();
    return rect.left + (rect.right - rect.left) / 2 - 50;
  }

  private setArrowPosition(card: Element, Arrow: HTMLElement): void {
    const leftPosition = this.getArrowPosition(card);
    Arrow.style.left = `${leftPosition}px`;
  }

  private scrollToExpandedItem(container: HTMLElement, card: HTMLElement): void {
    let contentContainer: HTMLElement | undefined;

    if (this.devicetype === DeviceType.Desktop && container.closest(".grid-row") !== null) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_, rowContainer] = this.getRowBlockArea(card);
      contentContainer = rowContainer;
    } else {
      contentContainer = container;
    }

    const boundingRect = contentContainer?.getBoundingClientRect();
    if (!boundingRect) return;

    const viewPortInfo = isOutOfViewport(boundingRect, 100);
    if (!viewPortInfo) return;
    if (viewPortInfo.top) {
      // 20 - need to add some margins
      const moveDist = boundingRect.height + window.pageYOffset + 20 + GetYOutOfViewport(card, 20);

      scrollTo(moveDist, 450, "easeOutQuad");
    }
  }

  private setSameHeightOnAllItemsInRow(list: Array<Element>): void {
    let currentDataId: string | null = "";
    let splitList: HTMLElement[];
    list.forEach((element): void => {
      const row = element.closest(".grid-row");
      if (row === null) return;

      const dataId = row.getAttribute("data-id");
      if (dataId !== currentDataId) {
        if (splitList && splitList.length > 1) {
          setSameMinHeightOnItems(splitList);
        }
        splitList = [];
        currentDataId = dataId;
        splitList.push(element as HTMLElement);
      } else {
        splitList.push(element as HTMLElement);
      }
    });

    if (splitList && splitList.length > 1) {
      setSameMinHeightOnItems(splitList);
    }
  }

  private resetColorArrowPosition = (): void => {
    const activeCardList = document.querySelectorAll(".faq-expandable-card.selected");
    activeCardList.forEach((el): void => {
      const row = el.closest(".grid-row");
      if (row === null) return;

      const arrowContainer = row.querySelector(".rowblock-expand-area");
      const jsArrow = arrowContainer?.querySelector(".js-arrow");
      if (!jsArrow) return;
      this.setArrowPosition(el, jsArrow.firstElementChild as HTMLElement);
    });
  };
}

document.addEventListener("DOMContentLoaded", () => {
  const faqExpandable = new FaqExpandable();
  faqExpandable.bootstrap();
});
